import { DotsHorizontalIcon } from '@radix-ui/react-icons'
import { Column } from '@tanstack/react-table'
import { Edit, TrashIcon } from 'lucide-react'
import { Row } from 'src/shared/components/Semantic/all'
import { Badge } from 'src/shared/components/ui/badge'
import { Button } from 'src/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/shared/components/ui/dropdown-menu'
import { Checkbox } from 'src/shared/components/ui/checkbox'
import dayjs from 'dayjs'
import { Roles, roleMap } from 'src/shared/consts'
import { TeamMember } from '../types'
import { SortableHeader } from './SortableHeader'

export const createTableColumns = ({
  onEditUser,
  onRemoveUser,
  t,
}: {
  onEditUser: (user: TeamMember) => void
  onRemoveUser: (orgUserId: number) => Promise<void>
  t: (en: string, sv?: string) => string
}) => {
  return [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={Boolean(
            table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
          )}
          onCheckedChange={(value) =>
            table.toggleAllPageRowsSelected(!!value)
          }
          aria-label={t('Select all', 'Välj alla')}
          label={''}
          setChecked={() => {}}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label={t('Select row', 'Välj rad')}
          label={''}
          setChecked={() => {}}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'first_name',
      header: ({ column }: { column: Column<TeamMember, unknown> }) => (
        <SortableHeader column={column} t={t}>
          {t('First name', 'Förnamn')}
        </SortableHeader>
      ),
      cell: ({ row }) => <div>{row.getValue('first_name') || '-'}</div>,
    },
    {
      accessorKey: 'last_name',
      header: ({ column }: { column: Column<TeamMember, unknown> }) => (
        <SortableHeader column={column} t={t}>
          {t('Last name', 'Efternamn')}
        </SortableHeader>
      ),
      cell: ({ row }) => <div>{row.getValue('last_name') || '-'}</div>,
    },
    {
      accessorKey: 'phone',
      header: ({ column }: { column: Column<TeamMember, unknown> }) => (
        <SortableHeader column={column} t={t}>
          {t('Phone', 'Telefon')}
        </SortableHeader>
      ),
      cell: ({ row }) => <div>{row.getValue('phone') || '-'}</div>,
    },
    {
      accessorKey: 'email',
      header: ({ column }: { column: Column<TeamMember, unknown> }) => (
        <SortableHeader column={column} t={t}>
          {t('Email', 'E-post')}
        </SortableHeader>
      ),
      cell: ({ row }) => <div>{row.getValue('email')}</div>,
    },
    {
      accessorKey: 'role_id',
      header: ({ column }: { column: Column<TeamMember, unknown> }) => (
        <SortableHeader column={column} t={t}>
          {t('Role', 'Roll')}
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <Row className="gap-1">
          <Badge variant="light">{roleMap[row.getValue('role_id')]}</Badge>
        </Row>
      ),
    },
    {
      accessorKey: 'joined',
      header: ({ column }: { column: Column<TeamMember, unknown> }) => (
        <SortableHeader column={column} t={t}>
          {t('Joined', 'Gick med')}
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <div>{dayjs(row.getValue('joined')).format('MMM D, YYYY')}</div>
      ),
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const teamMember = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">{t('Open menu', 'Öppna meny')}</span>
                <DotsHorizontalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEditUser(teamMember)}>
                <Edit className="mr-2 h-4 w-4" />
                {t('Edit', 'Redigera')}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={teamMember.role_id === Roles.SUPERADMIN}
                onClick={async () => {
                  await onRemoveUser(teamMember.org_user_id)
                }}
              >
                <TrashIcon className="mr-2 h-4 w-4" />
                {t('Remove from org', 'Ta bort från organisation')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]
}

